<script setup lang="ts">
import {onMounted, ref, watch, computed, defineModel} from "vue";
import {useApiCall} from "@/composables/useApiCall";
import yapi from "@/lib/yapi";
import {Tenant, Institution, Cohort} from "@YenzaCT/sdk";
import {debounce, isEqual} from "lodash";

type EntityType = "tenant" | "institution" | "cohort";
type EntityItem = Tenant | Institution | Cohort | string;

interface Props {
  multiple?: boolean;
  clearable?: boolean;
  showSearch?: boolean;
  returnObject?: boolean;
  entityType?: EntityType;
  disabled?: boolean;
  useStore?: boolean; // New prop to enable store integration

  tenantIds?: string[]; // Required for institutions
  institutionIds?: string[]; // Required for cohorts
}

const props = withDefaults(defineProps<Props>(), {
  multiple: false,
  clearable: true,
  showSearch: true,
  returnObject: false,
  disabled: false,
  entityType: "tenant",
});

const {executeApi} = useApiCall();
const page = ref(1);
const docs = ref<EntityItem[]>([]);
const hasNextPage = ref(false);
const loading = ref(false);
// const selected = ref<string[] | string | null>();
const selected = defineModel<string[] | string | null>();
const isSelecting = ref(false);
const searchText = ref<string>("");
const selectedTenants = computed(() => selected.value);
const selectedInstitutions = computed(() => selected.value);
const selectedCohorts = computed(() => selected.value);

const labelText = computed(() => {
  const labelMap = {
    institution: "Select Institution",
    cohort: "Select Cohort",
    tenant: "Select Tenant",
  };

  return labelMap[props.entityType] || "Search and Select Tenant";
});

// Create debounced search function
const debouncedSearch = debounce((value: string) => {
  // Reset pagination when searching
  page.value = 1;
  docs.value = [];
  fetchItems(value);
}, 500);

// Watch for search text changes
watch(searchText, (newValue) => {
  debouncedSearch(newValue);
});

// Watch for selection changes to set the flag
watch(selected, () => {
  isSelecting.value = true;
});

// Get filter IDs from store or props
const filterTenantIds = computed(() => props.tenantIds || []);

const filterInstitutionIds = computed(() => props.institutionIds || []);

watch(() => props.tenantIds, (newValue, oldValue) => {
  if (!isEqual(oldValue, newValue)) {
    page.value = 1;
    docs.value = [];
    selected.value = null;
    fetchItems(searchText.value);
  }
});

watch(() => props.institutionIds, (newValue, oldValue) => {
  if (!isEqual(oldValue, newValue)) {
    page.value = 1;
    docs.value = [];
    selected.value = null;
    fetchItems(searchText.value);
  }
});

watch(() => selectedTenants.value, (newValue: string | undefined) => {
  if (props.entityType === "tenant" && (!newValue || newValue.length === 0))
    selected.value = null;
});

watch(() => selectedInstitutions.value, (newValue: string | undefined) => {
  if (props.entityType === "institution" && (!newValue || newValue.length === 0))
    selected.value = null;
});

watch(() => selectedCohorts.value, (newValue: string | undefined) => {
  if (props.entityType === "cohort" && (!newValue || newValue.length === 0))
    selected.value = null;
});

onMounted(async () => {
  await fetchItems();
});

const emit = defineEmits<{
  (e: "clear"): void;
}>();

function handleClear() {
  searchText.value = "";
  emit("clear");
}

async function fetchItems(search?: string) {
  loading.value = true;

  const result = await executeApi(async () => {
    let res;

    switch (props.entityType) {
    case "institution":
      res = await yapi.admin.institution.paginate({
        page: page.value,
        size: 20,
        title: search || "",
        filterTenants: filterTenantIds.value && filterTenantIds.value.length > 0 ? filterTenantIds.value : undefined,
      });
      break;

    case "cohort":
      res = await yapi.admin.cohort.paginate({
        page: page.value,
        size: 20,
        title: search || "",
        filterTenants: filterTenantIds.value && filterTenantIds.value.length > 0 ? filterTenantIds.value : undefined,
        filterInstitutions: filterInstitutionIds.value && filterInstitutionIds.value.length > 0 ? filterInstitutionIds.value : undefined,
      });
      break;

    default: // tenant
      res = await yapi.admin.tenant.paginate({
        page: page.value,
        size: 20,
        title: search || ""
      });
      break;
    }

    return res?.data;
  });

  if (result) {
    docs.value.push(...result.docs);
    hasNextPage.value = result.hasNextPage;
  }

  loading.value = false;
}

async function loadNextPage() {
  if (hasNextPage.value && !loading.value) {
    page.value++;
    await fetchItems(searchText.value);
  }
}

</script>

<template>
  <v-autocomplete
    v-model="selected"
    :items="docs"
    item-title="title"
    item-value="_id"
    variant="outlined"
    density="compact"
    rounded="lg"
    :disabled="disabled"
    :label="labelText"
    :loading="loading"
    :multiple="multiple"
    :clearable="clearable"
    :return-object="returnObject"
    @click:clear="handleClear"
    min-width="250px"
  >
    <template v-slot:prepend-item>
      <v-text-field
        v-if="showSearch"
        v-model="searchText"
        placeholder="Search..."
        variant="outlined"
        density="compact"
        clearable
        rounded="lg"
        class="ma-2 mb-0"
        @keydown.stop
        @keyup.stop
      />
    </template>
    <template v-slot:append-item>
      <div v-if="hasNextPage" @click="loadNextPage" class="text-center bg-grey-lighten-4 py-2">
        <v-btn variant="tonal" color="primary" :loading="loading">Load More</v-btn>
      </div>
    </template>
  </v-autocomplete>
</template>

<style scoped>

</style>
